import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useField } from '@unform/core';
import Cropper from 'react-easy-crop';
import { MdInsertPhoto, MdDelete } from 'react-icons/md';
import { toast } from 'react-hot-toast';

import {
    Container,
    DefaultAvatar,
    InfoText,
    CropContainer,
    CropControls,
    ZoomControl,
    CropActions,
    ErrorText,
    ImageWrapper,
    DeleteButton,
} from './styles';
import api from '~/services/api';
import {
    IconTrash,
    IconTrashFilled,
    Modal as TakeatModal,
} from 'takeat-design-system-ui-kit';
import PersonalButton from '~/components/Buttons/PersonalButton';
import PersonalOutlineButton from '~/components/Buttons/PersonalOutlineButton';

const MAX_FILE_SIZE = 3 * 1024 * 1024; // 3MB

// Função para criar a imagem cortada
const createImage = (url) =>
    new Promise((resolve, reject) => {
        const image = new Image();
        image.addEventListener('load', () => resolve(image));
        image.addEventListener('error', (error) => reject(error));
        image.src = url;
    });

const getCroppedImg = async (imageSrc, pixelCrop) => {
    const image = await createImage(imageSrc);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    canvas.width = pixelCrop.width;
    canvas.height = pixelCrop.height;

    ctx.drawImage(
        image,
        pixelCrop.x,
        pixelCrop.y,
        pixelCrop.width,
        pixelCrop.height,
        0,
        0,
        pixelCrop.width,
        pixelCrop.height
    );

    return new Promise((resolve) => {
        canvas.toBlob((blob) => {
            resolve(blob);
        }, 'image/jpeg', 0.9);
    });
};

export default function ImageCropInput({
    name,
    product,
    products,
    setProducts,
    setProduct,
    ...rest
}) {
    const ref = useRef(null);
    const { fieldName, registerField, defaultValue } = useField(name);

    const [fileId, setFileId] = useState(defaultValue && defaultValue.id);
    const [preview, setPreview] = useState(defaultValue && defaultValue.url);
    const [error, setError] = useState('');

    // Estados do crop
    const [showCropModal, setShowCropModal] = useState(false);
    const [imageToCrop, setImageToCrop] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        registerField({
            name: fieldName,
            ref: ref.current,
            path: 'dataset.fileid',
            clearValue() {
                if (ref.value) {
                    ref.value = '';
                }
                setPreview(null);
            },
        });
    }, [fieldName, registerField]);

    useEffect(() => {
        if (defaultValue) {
            setPreview(defaultValue.url);
            setFileId(defaultValue.id);
        }
    }, [defaultValue]);

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setError('');

        // Validar tamanho
        if (file.size > MAX_FILE_SIZE) {
            setError('A imagem deve ter no máximo 3MB');
            toast.error('A imagem deve ter no máximo 3MB');
            e.target.value = '';
            return;
        }

        // Validar tipo
        if (!file.type.startsWith('image/')) {
            setError('Selecione um arquivo de imagem válido');
            toast.error('Selecione um arquivo de imagem válido');
            e.target.value = '';
            return;
        }

        // Criar URL para preview e abrir modal de crop
        const reader = new FileReader();
        reader.onload = () => {
            setImageToCrop(reader.result);
            setShowCropModal(true);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    };

    const handleCropConfirm = async () => {
        try {
            setUploading(true);
            const croppedBlob = await getCroppedImg(imageToCrop, croppedAreaPixels);

            // Verificar tamanho após crop
            if (croppedBlob.size > MAX_FILE_SIZE) {
                toast.error('Imagem muito grande após o corte. Tente reduzir o zoom.');
                setUploading(false);
                return;
            }

            const data = new FormData();
            data.append('file', croppedBlob, 'product-image.jpg');

            const response = await api.post('restaurants/files', data);
            toast.success('Imagem incluída com sucesso');

            const { id, url, name: nameData, path, updatedAt } = response.data;

            setFileId(id);
            setPreview(url);
            setShowCropModal(false);
            setImageToCrop(null);

            if (products) {
                const productFind = products.findIndex(
                    (prod) => prod.id === product.id
                );

                const newProductsArray = [...products];
                const imageData = { url, id, name: nameData, path, updatedAt };

                newProductsArray[productFind] = {
                    ...product,
                    image: imageData,
                };

                setProducts([...newProductsArray]);
                setProduct({ ...product, image: imageData });
            }
        } catch (err) {
            toast.error('Erro ao atualizar imagem');
            console.log(err);
        } finally {
            setUploading(false);
        }
    };

    const handleCropCancel = () => {
        setShowCropModal(false);
        setImageToCrop(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
    };

    const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

    async function handleDeleteProductImage() {
        try {
            setIsOpenDeleteModal(false);
            const response = await api.delete(`restaurants/files/${fileId}`);

            if (response.status === 200) {
                toast.success('Imagem excluída com sucesso');
                setFileId(null);
                setPreview(null);
                setProduct((prev) => ({ ...prev, image: null }));

                const productFind = products.findIndex(
                    (prod) => prod.id === product.id
                );
                const newProductsArray = [...products];
                newProductsArray[productFind] = { ...product, image: null };
                setProducts([...newProductsArray]);
            } else {
                toast.error('Erro ao deletar imagem');
            }
        } catch (e) {
            toast.error('Erro ao deletar imagem');
        }
    }

    return (
        <>
            <Container>
                <ImageWrapper>
                    <label htmlFor="avatar-crop">
                        {preview ? (
                            <img src={preview} alt="Foto do produto" />
                        ) : (
                            <DefaultAvatar>
                                <MdInsertPhoto size={45} color="#ddd" />
                                <span>Adicionar Foto</span>
                            </DefaultAvatar>
                        )}
                        <input
                            type="file"
                            id="avatar-crop"
                            accept="image/*"
                            data-fileid={fileId}
                            onChange={handleFileSelect}
                            ref={ref}
                            {...rest}
                        />
                    </label>
                    {fileId && (
                        <DeleteButton
                            type="button"
                            onClick={(e) => {
                                e.preventDefault();
                                setIsOpenDeleteModal(true);
                            }}
                        >
                            <IconTrashFilled />
                        </DeleteButton>
                    )}
                </ImageWrapper>
                <InfoText>Tamanho máximo: 3MB</InfoText>
                {error && <ErrorText>{error}</ErrorText>}
            </Container>

            {/* Modal de Crop */}
            <TakeatModal
                open={showCropModal}
                toggle={handleCropCancel}
                style={{ width: '450px', maxWidth: '95vw', height: 'fit-content', padding: 20 }}

            >
                <TakeatModal.Header>
                    <h3 style={{ margin: 0 }}>Ajustar imagem</h3>
                    <p style={{ color: '#666', fontSize: 14, margin: '8px 0 0 0' }}>
                        Arraste para posicionar e use o zoom para ajustar
                    </p>
                </TakeatModal.Header>
                <TakeatModal.Body>
                    <CropContainer>
                        {imageToCrop && (
                            <Cropper
                                image={imageToCrop}
                                crop={crop}
                                zoom={zoom}
                                aspect={1}
                                onCropChange={setCrop}
                                onCropComplete={onCropComplete}
                                onZoomChange={setZoom}
                            />
                        )}
                    </CropContainer>
                    <CropControls>
                        <ZoomControl>
                            <span>Zoom</span>
                            <input
                                type="range"
                                value={zoom}
                                min={1}
                                max={3}
                                step={0.1}
                                onChange={(e) => setZoom(Number(e.target.value))}
                            />
                        </ZoomControl>
                    </CropControls>
                    <CropActions>
                        <PersonalButton
                            color="#FF2C3A"
                            message="Cancelar"
                            onClick={handleCropCancel}
                            type="button"
                        />
                        <PersonalButton
                            color="#0CCAB4"
                            message={uploading ? 'Enviando...' : 'Confirmar'}
                            onClick={handleCropConfirm}
                            type="button"
                            disabled={uploading}
                        />
                    </CropActions>
                </TakeatModal.Body>
            </TakeatModal>

            {/* Modal de confirmação de exclusão */}
            <TakeatModal
                open={isOpenDeleteModal}
                style={{ width: '114px', height: 'fit-content', zIndex: '20 !important', padding: 20 }}
            >
                <TakeatModal.Header>
                    Tem certeza de que deseja excluir essa imagem?
                </TakeatModal.Header>
                <TakeatModal.Footer>
                    <PersonalButton
                        color="#FF2C3A"
                        message="Cancelar"
                        style={{ marginLeft: 0 }}
                        onClick={() => setIsOpenDeleteModal(false)}
                    />
                    <PersonalOutlineButton
                        color="#fff"
                        fontColor="#0CCAB4"
                        borderColor="#0CCAB4"
                        message="Confirmar"
                        type="button"
                        onClick={handleDeleteProductImage}
                    />
                </TakeatModal.Footer>
            </TakeatModal>
        </>
    );
}
